import {
  Component,
  ElementRef,
  EventEmitter,
  Input,
  OnDestroy,
  OnInit,
  Output,
  PLATFORM_ID,
  ViewChild,
  inject,
  ChangeDetectionStrategy,
} from '@angular/core';
import { UntypedFormGroup, ValidationErrors } from '@angular/forms';
import { DOCUMENT, isPlatformBrowser } from '@angular/common';

import { Subscription } from 'rxjs';

import { trimAndReplaceWhitespace, unsubscribe } from '@fcom/core/utils';

@Component({
  selector: 'fin-input-field',
  templateUrl: './input-field.component.html',
  changeDetection: ChangeDetectionStrategy.Eager,
  standalone: false,
})
export class InputFieldComponent implements OnInit, OnDestroy {
  private document = inject<Document>(DOCUMENT);
  private platform = inject(PLATFORM_ID);

  @ViewChild('inputElement', { static: true })
  inputElement: ElementRef;

  @Output()
  focus: EventEmitter<string> = new EventEmitter<string>();
  @Output()
  blur: EventEmitter<string> = new EventEmitter<string>();
  @Output()
  change: EventEmitter<string> = new EventEmitter<string>();

  @Input()
  group: UntypedFormGroup;
  @Input()
  hideError: boolean;
  @Input()
  modelName: string;
  @Input()
  name: string;
  @Input()
  autocomplete: string;
  @Input()
  hiddenLabel: string;
  @Input()
  translationPrefix: string;
  @Input()
  required: boolean;
  @Input()
  disabled: boolean;
  @Input()
  trim = true;

  @Input()
  set type(type: string) {
    if (type === 'tel') {
      this.inputPattern = '[0-9]*';
    }
    this.inputType = type || 'text';
  }

  inputPattern: string = undefined;
  inputType: string;

  subscription: Subscription;

  ngOnInit(): void {
    this.subscription = this.group.get(this.modelName).valueChanges.subscribe((s) => {
      if (isPlatformBrowser(this.platform) && this.inputElement.nativeElement !== this.document.activeElement) {
        this.inputElement.nativeElement.value = s;
      }
    });
  }

  ngOnDestroy(): void {
    this.subscription = unsubscribe(this.subscription);
  }

  firstError(): { key: string | undefined; data: Record<string, string | number> | undefined } {
    const errors: ValidationErrors | null = this.group.get(this.modelName).errors;
    if (!errors) {
      return { key: undefined, data: undefined };
    }
    const key = Object.keys(errors).filter((k) => errors[k])[0];
    return {
      key,
      data: errors[key] as Record<string, string | number> | undefined,
    };
  }

  onChangeEvent(event: Event): void {
    const field = this.group.get(this.modelName);

    // Chrome on iOS does not trigger the "input" event on autofill.
    if ((event.target as HTMLInputElement).value !== field.value) {
      field.setValue((event.target as HTMLInputElement).value);
    }

    this.change.emit([this.translationPrefix, 'info'].join('.'));
  }

  onBlur(): void {
    const control = this.group.get(this.modelName);

    if (this.trim) {
      control.setValue(trimAndReplaceWhitespace(control.getRawValue()));
    }

    this.blur.emit([this.translationPrefix, 'info'].join('.'));
  }

  onEnter(): void {
    const control = this.group.get(this.modelName);

    if (this.trim) {
      control.setValue(trimAndReplaceWhitespace(control.getRawValue()));
    }
  }

  onPaste(): void {
    const control = this.group.get(this.modelName);

    if (!this.trim) {
      return;
    }

    // Without this the field value does not include the pasted content.
    setTimeout(() => {
      control.setValue(trimAndReplaceWhitespace(control.getRawValue()));
    }, 0);
  }
}
